/**
 * <announcement-bar-countdown> custom element.
 *
 * Renders a fixed-duration countdown that starts from the merchant-configured
 * H/M/S each time the page loads, ticks down at 1Hz, and AUTO-LOOPS back to
 * the configured duration when it hits zero — so visitors always see a count
 * close to the configured value ("always 02h:17min remaining" pattern).
 *
 * No persistence; each page load is its own fresh countdown. Designed for
 * "artificial urgency" — pure marketing nudge, not tied to any real deadline.
 *
 * Server-rendered initial values (in Liquid) are overwritten on hydration so
 * cached HTML can't show stale counts.
 */
(function () {
  'use strict';

  if (window.customElements && customElements.get('announcement-bar-countdown')) return;

  function pad2(n) {
    return String(n).padStart(2, '0');
  }

  class AnnouncementBarCountdown extends HTMLElement {
    connectedCallback() {
      const h = parseInt(this.dataset.displayHours, 10) || 0;
      const m = parseInt(this.dataset.displayMinutes, 10) || 0;
      const s = parseInt(this.dataset.displaySeconds, 10) || 0;
      this.initialSeconds = h * 3600 + m * 60 + s;

      // Safety guard — at least 1 minute. Without this, a misconfigured
      // 0/0/0 would divide-by-zero in the modulo math below.
      if (this.initialSeconds <= 0) this.initialSeconds = 60;

      this.startTimestamp = Date.now();

      this.hoursEl = this.querySelector('[data-acb-hours]');
      this.minutesEl = this.querySelector('[data-acb-minutes]');
      this.secondsEl = this.querySelector('[data-acb-seconds]');

      // First tick immediately so the server-rendered values are replaced
      // with the actual ticking values before the visitor's eyes settle.
      this.tick();

      // 1Hz updates. Looping is handled inside `tick()` via modulo math.
      this.timer = window.setInterval(() => this.tick(), 1000);

      // Pause-and-resync on visibility loss/restore. Saves CPU while the
      // tab is backgrounded; instant resync when the visitor switches back
      // (otherwise setInterval throttling can show a drifted count for a
      // few seconds after focus returns).
      this._onVisibility = () => {
        if (document.visibilityState === 'visible') this.tick();
      };
      document.addEventListener('visibilitychange', this._onVisibility);
    }

    disconnectedCallback() {
      if (this.timer) window.clearInterval(this.timer);
      if (this._onVisibility) {
        document.removeEventListener('visibilitychange', this._onVisibility);
      }
    }

    tick() {
      // Time spent on this page since the component mounted.
      const elapsed = Math.floor((Date.now() - this.startTimestamp) / 1000);

      // Modulo arithmetic makes the loop free: when `elapsed` reaches
      // `initialSeconds`, position resets to 0 and remaining resets to
      // `initialSeconds`. So the countdown ticks down → hits 1 → snaps
      // back to the configured H/M/S and continues. No state flag needed.
      const positionInCycle = elapsed % this.initialSeconds;
      const remaining = this.initialSeconds - positionInCycle;

      const h = Math.floor(remaining / 3600);
      const m = Math.floor((remaining % 3600) / 60);
      const s = remaining % 60;

      if (this.hoursEl) this.hoursEl.textContent = pad2(h);
      if (this.minutesEl) this.minutesEl.textContent = pad2(m);
      if (this.secondsEl) this.secondsEl.textContent = pad2(s);
    }
  }

  customElements.define('announcement-bar-countdown', AnnouncementBarCountdown);
})();
